/**
 * OoT item definitions and grid layout.
 * Uses image assets from the existing /core/assets/oot/ directories.
 * Each item is wired through OotItem → TrackerContext for state persistence.
 */

import { OotItem } from './oot-item';
import { UpgradeItem } from './upgrade-item';
import { Item } from './item';
import { MultiLabelItem } from './multi-label-item';
import { TokenCounter } from './token-counter';
import { useTracker } from '@/contexts/tracker-context';
import { MEDALLION_ID_TO_REWARD_ID, useDerivedRewardZone } from '@/ootr/warp/entrance-bridge';

// ─── Image imports (gshd = HD icons) ──────────────────────────

// Items (gshd = HD icons with clean filenames)
import Bow from '@/core/assets/oot/gshd/bow_32x32.png';
import Hookshot from '@/core/assets/oot/gshd/hookshot-32x32.png';
import Longshot from '@/core/assets/oot/gshd/longshot_32x32.png';
import Token from '@/core/assets/oot/gshd/collected-skulltulas_32x32.png';

// Classic icons (fallback for items without gshd)
import Bomb from '@/core/assets/oot/gshd/bombs_32x32.png';
import Slingshot from '@/core/assets/oot/gshd/slingshot_32x32.png';
import Boomerang from '@/core/assets/oot/gshd/boomerang_32x32.png';
import DekuStick from '@/core/assets/oot/gshd/sticks_32x32.png';
import DekuNut from '@/core/assets/oot/gshd/nuts_32x32.png';
import DinsFire from '@/core/assets/oot/gshd/dins-fire_32x32.png';
import FaroresWind from '@/core/assets/oot/gshd/farores-wind_32x32.png';
import NayrusLove from '@/core/assets/oot/gshd/nayrus-love_32x32.png';
import FireArrow from '@/core/assets/oot/gshd/fire-arrows_32x32.png';
import IceArrow from '@/core/assets/oot/gshd/ice-arrows_32x32.png';
import LightArrow from '@/core/assets/oot/gshd/light-arrows_32x32.png';
import LensOfTruth from '@/core/assets/oot/gshd/lens_32x32.png';
import MagicBeans from '@/core/assets/oot/gshd/beans_32x32.png';
import MegatonHammer from '@/core/assets/oot/gshd/hammer_32x32.png';
import Bottle from '@/core/assets/oot/gshd/bottle-empty_32x32.png';
import Ocarina from '@/core/assets/oot/gshd/ocarina_32x32.png';
import OcarinaOfTime from '@/core/assets/oot/gshd/ocarina-of-time_32x32.png';
import Bombchu from '@/core/assets/oot/gshd/bomb-chu_32x32.png';
import KokiriSword from '@/core/assets/oot/gshd/kokiri-sword_32x32.png';
import MasterSword from '@/core/assets/oot/gshd/master-sword_32x32.png';
import BiggoronSword from '@/core/assets/oot/gshd/biggoron-sword_32x32.png';
import DekuShield from '@/core/assets/oot/gshd/deku-shield_32x32.png';
import HylianShield from '@/core/assets/oot/gshd/hylian-shield_32x32.png';
import MirrorShield from '@/core/assets/oot/gshd/mirror-shield_32x32.png';
import GoronTunic from '@/core/assets/oot/gshd/goron-tunic_32x32.png';
import ZoraTunic from '@/core/assets/oot/gshd/zora-tunic_32x32.png';
import KokiriTunic from '@/core/assets/oot/gshd/kokiri-tunic_32x32.png';
import IronBoots from '@/core/assets/oot/gshd/iron-boots_32x32.png';
import HoverBoots from '@/core/assets/oot/gshd/hover-boots_32x32.png';
import KokiriBoots from '@/core/assets/oot/gshd/kokiri-boots_32x32.png';
import GerudoCard from '@/core/assets/oot/gshd/gerudo-card_32x32.png';
import StoneOfAgony from '@/core/assets/oot/gshd/stone-of-agony_32x32.png';
import Magic from '@/core/assets/oot/gshd/magic_32x32.png';
import DoubleMagic from '@/core/assets/oot/gshd/double-magic_32x32.png';
import Wallet from '@/core/assets/oot/gshd/wallet2_32x32.png';
import GiantWallet from '@/core/assets/oot/gshd/wallet3_32x32.png';
import Scale from '@/core/assets/oot/gshd/scale_32x32.png';
import GoldenScale from '@/core/assets/oot/gshd/golden-scale_32x32.png';
import Strength1 from '@/core/assets/oot/gshd/strength_32x32.png';
import Strength2 from '@/core/assets/oot/gshd/strength2_32x32.png';
import Strength3 from '@/core/assets/oot/gshd/strength3_32x32.png';
import RutoLetter from '@/core/assets/oot/gshd/bottle-rutos-letter_32x32.png';

// Child Trade Sequence (gshd)
import WeirdEgg from '@/core/assets/oot/gshd/egg_32x32.png';
import Chicken from '@/core/assets/oot/gshd/chicken_32x32.png';
import ZeldasLetter from '@/core/assets/oot/gshd/zeldas-letter_32x32.png';
import KeatonMask from '@/core/assets/oot/gshd/keaton-mask_32x32.png';
import SkullMask from '@/core/assets/oot/gshd/skull-mask_32x32.png';
import SpookyMask from '@/core/assets/oot/gshd/spooky-mask_32x32.png';
import BunnyHood from '@/core/assets/oot/gshd/bunny-hood_32x32.png';
import GoronMask from '@/core/assets/oot/gshd/goron-mask_32x32.png';
import ZoraMask from '@/core/assets/oot/gshd/zora-mask_32x32.png';
import GerudoMask from '@/core/assets/oot/gshd/gerudo-mask_32x32.png';
import MaskOfTruth from '@/core/assets/oot/gshd/mask-of-truth_32x32.png';

// Medallions & Stones (gshd)
import ForestMedallion from '@/core/assets/oot/gshd/green-medallion_32x32.png';
import FireMedallion from '@/core/assets/oot/gshd/red-medallion_32x32.png';
import WaterMedallion from '@/core/assets/oot/gshd/blue-medallion_32x32.png';
import ShadowMedallion from '@/core/assets/oot/gshd/purple-medallion_32x32.png';
import SpiritMedallion from '@/core/assets/oot/gshd/orange-medallion_32x32.png';
import LightMedallion from '@/core/assets/oot/gshd/yellow-medallion_32x32.png';
import KokiriEmerald from '@/core/assets/oot/gshd/kokiri-stone_32x32.png';
import GoronRuby from '@/core/assets/oot/gshd/goron-stone_32x32.png';
import ZoraSapphire from '@/core/assets/oot/gshd/zora-stone_32x32.png';

// Songs (gshd)
import ZeldaLullaby from '@/core/assets/oot/gshd/zeldas-lullaby_32x32.png';
import EponaSong from '@/core/assets/oot/gshd/epona_32x32.png';
import SariaSong from '@/core/assets/oot/gshd/saria_32x32.png';
import SunSong from '@/core/assets/oot/gshd/suns-song_32x32.png';
import SongOfTime from '@/core/assets/oot/gshd/song-of-time_32x32.png';
import SongOfStorms from '@/core/assets/oot/gshd/song-of-storms_32x32.png';
import Minuet from '@/core/assets/oot/gshd/minuet_32x32.png';
import Bolero from '@/core/assets/oot/gshd/bolero_32x32.png';
import Serenade from '@/core/assets/oot/gshd/serenade_32x32.png';
import Requiem from '@/core/assets/oot/gshd/requiem_32x32.png';
import Nocturne from '@/core/assets/oot/gshd/nocturne_32x32.png';
import Prelude from '@/core/assets/oot/gshd/prelude_32x32.png';

// ─── Item Definition ───────────────────────────────────────────

interface ItemDef {
  id: string;
  type: 'simple' | 'upgrade' | 'medallion' | 'counter';
  image: string | string[];
  title: string | string[];
  upgradeLabel?: string[];
  labels?: string[];  // For medallion boss labels
}

const BOSS_LABELS = ['Free', 'Deku', 'DC', 'Jabu', 'Forest', 'Fire', 'Water', 'Shadow', 'Spirit'];

// Full classic item grid (6 columns × 8 rows)
const ITEMS_GRID: (ItemDef | null)[][] = [
  // Row 1
  [
    { id: 'Oot_DekuStick', type: 'simple', image: DekuStick, title: 'Deku Stick' },
    { id: 'Oot_DekuNut', type: 'simple', image: DekuNut, title: 'Deku Nut' },
    { id: 'Oot_Bomb', type: 'upgrade', image: [Bomb, Bomb, Bomb], title: ['Bombs', 'Big Bomb Bag', 'Biggest Bomb Bag'], upgradeLabel: ['', '30', '40'] },
    { id: 'Oot_Bow', type: 'upgrade', image: [Bow, Bow, Bow], title: ['Bow', 'Big Quiver', 'Giant Quiver'], upgradeLabel: ['', '40', '50'] },
    { id: 'Oot_FireArrow', type: 'simple', image: FireArrow, title: 'Fire Arrow' },
    { id: 'Oot_DinsFire', type: 'simple', image: DinsFire, title: "Din's Fire" },
  ],
  // Row 2
  [
    { id: 'Oot_Slingshot', type: 'upgrade', image: [Slingshot, Slingshot, Slingshot], title: ['Slingshot', 'Big Bullet Bag', 'Biggest Bullet Bag'], upgradeLabel: ['', '40', '50'] },
    { id: 'Oot_Ocarina', type: 'upgrade', image: [Ocarina, OcarinaOfTime], title: ['Fairy Ocarina', 'Ocarina of Time'] },
    { id: 'Oot_Bombchu', type: 'simple', image: Bombchu, title: 'Bombchu' },
    { id: 'Oot_Hookshot', type: 'upgrade', image: [Hookshot, Longshot], title: ['Hookshot', 'Longshot'], upgradeLabel: ['Hook', 'Long'] },
    { id: 'Oot_IceArrow', type: 'simple', image: IceArrow, title: 'Ice Arrow' },
    { id: 'Oot_FaroresWind', type: 'simple', image: FaroresWind, title: "Farore's Wind" },
  ],
  // Row 3
  [
    { id: 'Oot_Boomerang', type: 'simple', image: Boomerang, title: 'Boomerang' },
    { id: 'Oot_Lens', type: 'simple', image: LensOfTruth, title: 'Lens of Truth' },
    { id: 'Oot_MagicBeans', type: 'simple', image: MagicBeans, title: 'Magic Beans' },
    { id: 'Oot_Hammer', type: 'simple', image: MegatonHammer, title: 'Megaton Hammer' },
    { id: 'Oot_LightArrow', type: 'simple', image: LightArrow, title: 'Light Arrow' },
    { id: 'Oot_NayrusLove', type: 'simple', image: NayrusLove, title: "Nayru's Love" },
  ],
  // Row 4
  [
    { id: 'Oot_Bottle1', type: 'simple', image: Bottle, title: 'Bottle 1' },
    { id: 'Oot_Bottle2', type: 'simple', image: Bottle, title: 'Bottle 2' },
    { id: 'Oot_Bottle3', type: 'simple', image: Bottle, title: 'Bottle 3' },
    { id: 'Oot_RutoLetter', type: 'simple', image: RutoLetter, title: "Ruto's Letter" },
    {
      id: 'Oot_ChildTrade',
      type: 'upgrade',
      image: [WeirdEgg, Chicken, ZeldasLetter, KeatonMask, SkullMask, SpookyMask, BunnyHood, GoronMask, ZoraMask, GerudoMask, MaskOfTruth],
      title: ['Weird Egg', 'Chicken', "Zelda's Letter", 'Keaton Mask', 'Skull Mask', 'Spooky Mask', 'Bunny Hood', 'Goron Mask', 'Zora Mask', 'Gerudo Mask', 'Mask of Truth'],
    },
    null,
  ],
  // Row 5 (swords + token)
  [
    { id: 'Oot_KokiriSword', type: 'simple', image: KokiriSword, title: 'Kokiri Sword' },
    { id: 'Oot_MasterSword', type: 'simple', image: MasterSword, title: 'Master Sword' },
    { id: 'Oot_BiggoronSword', type: 'simple', image: BiggoronSword, title: "Giant's Knife / Biggoron" },
    { id: 'Oot_Token', type: 'counter', image: Token, title: 'Skulltula Tokens' },
    null,
    null,
  ],
  // Row 6 (shields + magic + wallets)
  [
    { id: 'Oot_DekuShield', type: 'simple', image: DekuShield, title: 'Deku Shield' },
    { id: 'Oot_HylianShield', type: 'simple', image: HylianShield, title: 'Hylian Shield' },
    { id: 'Oot_MirrorShield', type: 'simple', image: MirrorShield, title: 'Mirror Shield' },
    { id: 'Oot_Magic', type: 'upgrade', image: [Magic, DoubleMagic], title: ['Magic', 'Double Magic'] },
    { id: 'Oot_Wallet', type: 'upgrade', image: [Wallet, GiantWallet], title: ["Adult's Wallet", "Giant's Wallet"] },
    { id: 'Oot_GerudoCard', type: 'simple', image: GerudoCard, title: 'Gerudo Card' },
  ],
  // Row 7 (tunics + other)
  [
    { id: 'Oot_KokiriTunic', type: 'simple', image: KokiriTunic, title: 'Kokiri Tunic' },
    { id: 'Oot_GoronTunic', type: 'simple', image: GoronTunic, title: 'Goron Tunic' },
    { id: 'Oot_ZoraTunic', type: 'simple', image: ZoraTunic, title: 'Zora Tunic' },
    { id: 'Oot_StoneOfAgony', type: 'simple', image: StoneOfAgony, title: 'Stone of Agony' },
    { id: 'Oot_Scale', type: 'upgrade', image: [Scale, GoldenScale], title: ['Silver Scale', 'Golden Scale'] },
    { id: 'Oot_Strength', type: 'upgrade', image: [Strength1, Strength2, Strength3], title: ["Goron's Bracelet", 'Silver Gauntlets', 'Golden Gauntlets'] },
  ],
  // Row 8 (boots + spiritual stones)
  [
    { id: 'Oot_KokiriBoots', type: 'simple', image: KokiriBoots, title: 'Kokiri Boots' },
    { id: 'Oot_IronBoots', type: 'simple', image: IronBoots, title: 'Iron Boots' },
    { id: 'Oot_HoverBoots', type: 'simple', image: HoverBoots, title: 'Hover Boots' },
    { id: 'Oot_KokiriEmerald', type: 'medallion', image: KokiriEmerald, title: 'Kokiri Emerald', labels: BOSS_LABELS },
    { id: 'Oot_GoronRuby', type: 'medallion', image: GoronRuby, title: 'Goron Ruby', labels: BOSS_LABELS },
    { id: 'Oot_ZoraSapphire', type: 'medallion', image: ZoraSapphire, title: 'Zora Sapphire', labels: BOSS_LABELS },
  ],
  // Row 9 (medallions)
  [
    { id: 'Oot_ForestMedallion', type: 'medallion', image: ForestMedallion, title: 'Forest Medallion', labels: BOSS_LABELS },
    { id: 'Oot_FireMedallion', type: 'medallion', image: FireMedallion, title: 'Fire Medallion', labels: BOSS_LABELS },
    { id: 'Oot_WaterMedallion', type: 'medallion', image: WaterMedallion, title: 'Water Medallion', labels: BOSS_LABELS },
    { id: 'Oot_ShadowMedallion', type: 'medallion', image: ShadowMedallion, title: 'Shadow Medallion', labels: BOSS_LABELS },
    { id: 'Oot_SpiritMedallion', type: 'medallion', image: SpiritMedallion, title: 'Spirit Medallion', labels: BOSS_LABELS },
    { id: 'Oot_LightMedallion', type: 'medallion', image: LightMedallion, title: 'Light Medallion', labels: BOSS_LABELS },
  ],
  // Row 10 (basic ocarina songs)
  [
    { id: 'Oot_ZeldaLullaby', type: 'simple', image: ZeldaLullaby, title: "Zelda's Lullaby" },
    { id: 'Oot_EponaSong', type: 'simple', image: EponaSong, title: "Epona's Song" },
    { id: 'Oot_SariaSong', type: 'simple', image: SariaSong, title: "Saria's Song" },
    { id: 'Oot_SunSong', type: 'simple', image: SunSong, title: 'Sun Song' },
    { id: 'Oot_SongOfTime', type: 'simple', image: SongOfTime, title: 'Song of Time' },
    { id: 'Oot_SongOfStorms', type: 'simple', image: SongOfStorms, title: 'Song of Storms' },
  ],
  // Row 11 (warp songs)
  [
    { id: 'Oot_MinuetOfForest', type: 'simple', image: Minuet, title: 'Minuet of Forest' },
    { id: 'Oot_BoleroOfFire', type: 'simple', image: Bolero, title: 'Bolero of Fire' },
    { id: 'Oot_SerenadeOfWater', type: 'simple', image: Serenade, title: 'Serenade of Water' },
    { id: 'Oot_RequiemOfSpirit', type: 'simple', image: Requiem, title: 'Requiem of Spirit' },
    { id: 'Oot_NocturneOfShadow', type: 'simple', image: Nocturne, title: 'Nocturne of Shadow' },
    { id: 'Oot_PreludeOfLight', type: 'simple', image: Prelude, title: 'Prelude of Light' },
  ],
];

// ─── Medallion grid item (derives zone label from entrance tracker) ───

function MedallionGridItem({ def }: { def: ItemDef }) {
  const rewardId = MEDALLION_ID_TO_REWARD_ID[def.id];
  const derivedZone = useDerivedRewardZone(rewardId);
  return (
    <OotItem id={def.id}>
      {({ updateItem }) => (
        <MultiLabelItem
          id={def.id}
          image={def.image as string}
          title={def.title as string}
          labels={def.labels!}
          overrideLabel={derivedZone}
          onToggle={(obtained) => updateItem({ isObtained: obtained })}
        />
      )}
    </OotItem>
  );
}

// ─── Render helpers ────────────────────────────────────────────

function renderItem(def: ItemDef) {
  switch (def.type) {
    case 'upgrade':
      return (
        <OotItem key={def.id} id={def.id}>
          {({ item, updateItem }) => (
            <UpgradeItem
              id={def.id}
              image={def.image as string[]}
              title={def.title as string[]}
              upgradeLabel={def.upgradeLabel}
              initialState={item.state}
              onUpdate={(state, obtained) => updateItem({ state, isObtained: obtained })}
            />
          )}
        </OotItem>
      );

    case 'medallion':
      return <MedallionGridItem key={def.id} def={def} />;

    case 'counter':
      return <TokenCounter key={def.id} image={def.image as string} />;

    case 'simple':
    default:
      return (
        <OotItem key={def.id} id={def.id}>
          {({ item, updateItem }) => (
            <Item
              id={def.id}
              image={def.image as string}
              title={def.title as string}
              isObtained={item.isObtained}
              onToggle={(obtained) => updateItem({ isObtained: obtained })}
            />
          )}
        </OotItem>
      );
  }
}

// ─── Item Grid Component ──────────────────────────────────────

export function ItemGrid() {
  const { state } = useTracker();
  const isReduced = state.useItemTrackerReduce;

  return (
    <div className="space-y-4">
      {/* Main items grid */}
      <div className="inline-flex flex-col gap-0.5">
        {ITEMS_GRID.map((row, rowIdx) => {
          const hasLabel = row.some((def) => def?.type === 'medallion');
          const cellHeight = hasLabel ? 'h-14' : 'h-9';
          const cellAlign = hasLabel ? 'items-start' : 'items-center';
          return (
            <div key={rowIdx} className="flex gap-0.5">
              {row.map((def, colIdx) =>
                def ? (
                  <div key={def.id} className={`w-9 ${cellHeight} flex ${cellAlign} justify-center`}>
                    {renderItem(def)}
                  </div>
                ) : (
                  <div key={`empty-${rowIdx}-${colIdx}`} className={`w-9 ${cellHeight}`} />
                )
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
